/**
 * InstagramFeed — displays the 12 most recent @nokuhiro posts in a responsive grid.
 * Data is fetched server-side via tRPC and cached for 1 hour.
 * Falls back gracefully when the API is not yet configured or unavailable.
 */
import { trpc } from "@/lib/trpc";
import { useLanguage } from "@/contexts/LanguageContext";
import { Heart, ExternalLink, Instagram } from "lucide-react";

// ─── Localised labels ────────────────────────────────────────────────────────
const LABELS: Record<string, {
  heading: string;
  sub: string;
  cta: string;
  viewPost: string;
  fallback: string;
}> = {
  en: {
    heading: "Follow Our Journey",
    sub: "Real moments from the Nokuhiro community — join us on Instagram",
    cta: "Follow @nokuhiro",
    viewPost: "View on Instagram",
    fallback: "Follow us on Instagram for the latest updates from the Nokuhiro community.",
  },
  de: {
    heading: "Folge unserer Reise",
    sub: "Echte Momente aus der Nokuhiro-Community — folge uns auf Instagram",
    cta: "@nokuhiro folgen",
    viewPost: "Auf Instagram ansehen",
    fallback: "Folge uns auf Instagram für die neuesten Updates aus der Nokuhiro-Community.",
  },
  es: {
    heading: "Sigue Nuestra Historia",
    sub: "Momentos reales de la comunidad Nokuhiro — únete a nosotros en Instagram",
    cta: "Seguir @nokuhiro",
    viewPost: "Ver en Instagram",
    fallback: "Síguenos en Instagram para las últimas novedades de la comunidad Nokuhiro.",
  },
  it: {
    heading: "Segui il Nostro Viaggio",
    sub: "Momenti reali dalla community Nokuhiro — seguici su Instagram",
    cta: "Segui @nokuhiro",
    viewPost: "Vedi su Instagram",
    fallback: "Seguici su Instagram per gli ultimi aggiornamenti dalla community Nokuhiro.",
  },
  fr: {
    heading: "Suivez Notre Aventure",
    sub: "Des moments authentiques de la communauté Nokuhiro — rejoignez-nous sur Instagram",
    cta: "Suivre @nokuhiro",
    viewPost: "Voir sur Instagram",
    fallback: "Suivez-nous sur Instagram pour les dernières nouvelles de la communauté Nokuhiro.",
  },
};

// ─── Skeleton card ────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="aspect-square rounded-xl bg-[#2D4A3A]/60 animate-pulse overflow-hidden">
      <div className="w-full h-full bg-gradient-to-br from-[#2D4A3A] to-[#1A3A2A]" />
    </div>
  );
}

// ─── Fallback (no token configured or API error) ──────────────────────────────
function FallbackBanner({ locale }: { locale: string }) {
  const l = LABELS[locale] ?? LABELS.en;
  return (
    <div className="flex flex-col items-center gap-6 py-12 text-center">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FD5949] to-[#D6249F] flex items-center justify-center shadow-lg">
        <Instagram className="w-8 h-8 text-white" />
      </div>
      <p className="text-[#FFF8E7]/70 max-w-md text-base leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
        {l.fallback}
      </p>
      <a
        href="https://www.instagram.com/nokuhiro/"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white shadow-lg hover:shadow-xl transition-shadow"
        style={{
          background: "linear-gradient(135deg, #FD5949 0%, #D6249F 50%, #285AEB 100%)",
          fontFamily: "'Fredoka One', sans-serif",
          fontSize: "1.1rem",
        }}
      >
        <Instagram className="w-5 h-5" />
        {l.cta}
      </a>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function InstagramFeed() {
  const { locale } = useLanguage();
  const l = LABELS[locale] ?? LABELS.en;

  const { data, isLoading, isError } = trpc.instagram.getPosts.useQuery(undefined, {
    staleTime: 60 * 60 * 1000, // treat as fresh for 1 hour (matches server cache)
    retry: 1,
  });

  const posts = data?.posts ?? [];
  const hasContent = !isLoading && !isError && posts.length > 0;
  const showFallback = !isLoading && (isError || posts.length === 0);

  return (
    <section className="relative py-20 sm:py-28 overflow-hidden bg-[#1A3A2A]">
      {/* Subtle forest texture overlay */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #7ED957 0%, transparent 50%), radial-gradient(circle at 80% 20%, #F5A623 0%, transparent 40%)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #FD5949, #D6249F)" }}>
              <Instagram className="w-4 h-4 text-white" />
            </div>
            <span className="text-[#D6249F] text-sm font-bold uppercase tracking-widest"
              style={{ fontFamily: "'Nunito', sans-serif" }}>
              @nokuhiro
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl text-[#FFF8E7] mb-4"
            style={{ fontFamily: "'Fredoka One', sans-serif" }}
          >
            {l.heading}
          </h2>
          <p className="text-[#FFF8E7]/60 text-lg max-w-xl mx-auto" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {l.sub}
          </p>
        </div>

        {/* Loading skeletons */}
        {isLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* Post grid */}
        {hasContent && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {posts.map((post) => (
              <a
                key={post.id}
                href={post.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative aspect-square rounded-xl overflow-hidden bg-[#2D4A3A] shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                aria-label={post.caption ? post.caption.slice(0, 80) : l.viewPost}
              >
                {/* Post image */}
                <img
                  src={post.imageUrl}
                  alt={post.caption ? post.caption.slice(0, 80) : "Nokuhiro Instagram post"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />

                {/* Video badge */}
                {post.mediaType === "VIDEO" && (
                  <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                  {post.caption && (
                    <p className="text-white text-xs leading-snug line-clamp-3 mb-2"
                      style={{ fontFamily: "'Nunito', sans-serif" }}>
                      {post.caption}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    {post.likes > 0 && (
                      <span className="flex items-center gap-1 text-white/80 text-xs">
                        <Heart className="w-3 h-3 fill-[#E74C3C] text-[#E74C3C]" />
                        {post.likes.toLocaleString()}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-[#F5A623] text-xs font-semibold ml-auto"
                      style={{ fontFamily: "'Nunito', sans-serif" }}>
                      <ExternalLink className="w-3 h-3" />
                      {l.viewPost}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Fallback */}
        {showFallback && <FallbackBanner locale={locale} />}

        {/* Follow CTA — shown only when posts are visible */}
        {hasContent && (
          <div className="mt-10 text-center">
            <a
              href="https://www.instagram.com/nokuhiro/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
              style={{
                background: "linear-gradient(135deg, #FD5949 0%, #D6249F 50%, #285AEB 100%)",
                fontFamily: "'Fredoka One', sans-serif",
                fontSize: "1.1rem",
              }}
            >
              <Instagram className="w-5 h-5" />
              {l.cta}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
